/* VitaPaw welcome card game. Fixed 10% bonus; newsletter posts stay native to Shopify. */
(() => {
  'use strict';
  if (customElements.get('vitapaw-scratch')) return;

  class VitaPawScratch extends HTMLElement {
    connectedCallback() {
      if (this._active) return;
      this._active = true;
      this._abort = new AbortController();
      this._timers = new Set();
      this._frames = new Set();
      if (!this.querySelector('[data-vp-dialog]') && document.readyState === 'loading') {
        this._on(document, 'DOMContentLoaded', () => this._init(), { once: true });
      } else {
        this._init();
      }
    }

    disconnectedCallback() {
      this._active = false;
      this._abort?.abort();
      this._timers?.forEach(clearTimeout);
      this._frames?.forEach(cancelAnimationFrame);
      if (this.dialog?.open) this.dialog.close();
      this._unlockScroll();
      this._initialized = false;
    }

    _on(target, type, listener, options = {}) {
      target?.addEventListener(type, listener, { ...options, signal: this._abort.signal });
    }

    _later(callback, delay) {
      const id = setTimeout(() => {
        this._timers.delete(id);
        if (this._active) callback();
      }, delay);
      this._timers.add(id);
      return id;
    }

    _frame(callback) {
      const id = requestAnimationFrame(() => {
        this._frames.delete(id);
        if (this._active) callback();
      });
      this._frames.add(id);
      return id;
    }

    _storage(kind, action, key, value) {
      try { return window[kind][action](key, value); } catch (_) { return null; }
    }

    _msg(key, values = {}) {
      let value = this._messages?.[key] || '';
      for (const [name, replacement] of Object.entries(values)) value = value.replaceAll(`{${name}}`, String(replacement));
      return value;
    }

    _init() {
      if (!this._active || this._initialized) return;
      try { this._messages = JSON.parse(this.querySelector('[data-vp-i18n]')?.textContent || '{}'); } catch (_) { this._messages = {}; }
      this.dialog = this.querySelector('[data-vp-dialog]');
      this.gameCards = Array.from(this.querySelectorAll('[data-vp-game-card]'));
      this.form = this.querySelector('[data-vp-form], .vp-newsletter-form');
      this.teaser = this.querySelector('[data-vp-open]');
      this.revealButton = this.querySelector('[data-vp-reveal]');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this._initialized = true;
      this.demo = this.dataset.demo === 'true';
      this.editor = this.dataset.editor === 'true';
      this.knownSubscriber = this.dataset.knownSubscriber === 'true';
      this.code = this.demo ? 'ANTEPRIMA10' : (this.dataset.code || '').trim();
      const campaign = this.dataset.campaign || 'welcome-v1';
      this._dismissKey = `vitapaw:${campaign}:seen`;
      this._successKey = `vitapaw:${campaign}:subscribed`;
      this._pendingKey = `vitapaw:${campaign}:pending`;
      this._seen = !this.demo && this._storage('sessionStorage', 'getItem', this._dismissKey) === '1';
      this._submittedBefore = !this.demo && this._storage('localStorage', 'getItem', this._successKey) === '1';
      this._restricted = !this.demo && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:cart|checkout|account)(?:\/|$)/i.test(location.pathname);
      const pendingTime = Number(this._storage('sessionStorage', 'getItem', this._pendingKey));
      const pendingAge = Date.now() - pendingTime;
      const pending = !this.demo && pendingTime > 0 && pendingAge >= 0 && pendingAge < 30 * 60 * 1000;
      this._serverVerified = pending && !!this.form?.querySelector('[data-vp-server-status="success"]');
      this._verified = this.knownSubscriber || this._serverVerified;
      this._serverError = pending && !!this.form?.querySelector('[data-vp-server-status="error"]');
      if (!this.demo && (this._serverVerified || this._serverError || (pendingTime > 0 && !pending))) this._storage('sessionStorage', 'removeItem', this._pendingKey);
      this._revealed = false;
      this._cardLabels = new Map(this.gameCards.map((card, index) => [card, card.getAttribute('aria-label') || this._msg('card_discover', {number: index + 1})]));
      this._resetCards();

      this.querySelectorAll('[data-vp-code]').forEach((node) => { node.textContent = this.code; });
      this.querySelectorAll('[data-vp-copy]').forEach((node) => { node.disabled = !this.code; });
      this.querySelectorAll('[data-vp-demo-note]').forEach((node) => {
        node.hidden = !this.demo;
      });
      this._setStage(this._verified ? 'reward' : this._serverError ? 'email' : 'scratch', false);
      if (this._serverVerified) this._storage('localStorage', 'setItem', this._successKey, '1');

      this._on(this.teaser, 'click', (event) => { event.preventDefault(); this.open(); });
      this.querySelectorAll('[data-vp-close], [data-vp-skip]').forEach((button) => {
        this._on(button, 'click', (event) => { event.preventDefault(); this.close(); });
      });
      this._on(this.dialog, 'cancel', (event) => { event.preventDefault(); this.close(); });
      this._on(this.dialog, 'close', () => this._afterClose());
      this._on(this.dialog, 'pointerdown', (event) => {
        this._backdropStart = event.target === this.dialog && this._outsideDialog(event);
      });
      this._on(this.dialog, 'click', (event) => {
        if (event.target === this.dialog && this._backdropStart && this._outsideDialog(event)) this.close();
        this._backdropStart = false;
      });
      this._on(this.revealButton, 'click', (event) => {
        event.preventDefault();
        this._reveal();
      });
      this.gameCards.forEach((card, index) => {
        this._on(card, 'click', (event) => {
          event.preventDefault();
          this._flipCard(card, index);
        });
      });
      this._on(this.form, 'submit', (event) => {
        if (!this.form.reportValidity()) { event.preventDefault(); return; }
        if (this.demo) {
          event.preventDefault();
          // Preview never submits, stores, or logs the address.
          this._setStage('reward');
          this.form.reset();
        } else {
          // Correlate a server response with this form, not another newsletter form.
          this._storage('sessionStorage', 'setItem', this._pendingKey, String(Date.now()));
        }
        // Live store: let Shopify handle submission, validation and any CAPTCHA.
      });
      this.querySelectorAll('[data-vp-copy]').forEach((button) => {
        this._on(button, 'click', (event) => { event.preventDefault(); this._copyCode(); });
      });
      this.querySelectorAll('[data-vp-shop]').forEach((link) => {
        this._on(link, 'click', (event) => {
          if (this.demo) {
            event.preventDefault();
            this._feedback(this._msg('demo_inactive'));
          }
        });
      });
      this._on(this, 'vp:reset', () => { if (this.demo) this.reset(); });
      if (this.demo) {
        this._on(document, 'click', (event) => {
          if (event.target instanceof Element && event.target.closest('[data-vp-demo-reset]')) this.reset();
        });
      }

      this._syncTeaser();
      if (this._restricted) return;
      if (this._serverVerified) {
        this._queueOpen(0, true);
      } else if (this._serverError) {
        this._queueOpen(0, true);
      } else if (!this.editor && !this.knownSubscriber && !this._submittedBefore && !this._seen) {
        const delay = Number.parseFloat(this.dataset.delay);
        this._queueOpen(this.demo ? 0 : Math.max(0, Number.isFinite(delay) ? delay : 15) * 1000);
      }
    }

    _visible(element) {
      if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
      const style = getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && element.getClientRects().length > 0;
    }

    _blocked() {
      if (document.hidden) return true;
      const banner = document.querySelector('#shopify-pc__banner');
      if (this._visible(banner)) return true;
      return Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => dialog !== this.dialog && this._visible(dialog));
    }

    _queueOpen(delay, afterServerResponse = false) {
      this._later(() => {
        if (this.dialog.open || this._restricted) return;
        if (!afterServerResponse && (this._seen || this._submittedBefore || this.knownSubscriber)) return;
        if (this._blocked()) { this._queueOpen(500, afterServerResponse); return; }
        this.open('auto');
      }, delay);
    }

    open(source = 'manual') {
      if (!this._initialized || this.dialog.open || this._restricted) return;
      if (this._blocked()) {
        if (source === 'auto') this._queueOpen(500);
        return;
      }
      this._previousFocus = document.activeElement;
      this.dialog.showModal();
      this._lockScroll();
      this._seen = true;
      if (!this.demo) this._storage('sessionStorage', 'setItem', this._dismissKey, '1');
      this._syncTeaser();
      this._frame(() => this._focusStage());
      this.dispatchEvent(new CustomEvent('vp:open', { bubbles: true, detail: { source } }));
    }

    close() {
      if (this.dialog?.open) this.dialog.close();
    }

    _afterClose() {
      this._unlockScroll();
      this._syncTeaser();
      if (this._previousFocus instanceof HTMLElement && this._previousFocus.isConnected && this._previousFocus !== document.body) {
        this._previousFocus.focus({ preventScroll: true });
      } else if (this.teaser && !this.teaser.hidden) {
        this.teaser.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('vp:close', { bubbles: true }));
    }

    _outsideDialog(event) {
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    _syncTeaser() {
      if (!this.teaser) return;
      this.teaser.hidden = this._restricted || this.dialog.open || (!this.demo && this.knownSubscriber) || (!this._seen && !this.editor && !this._verified) || (!this.demo && this._submittedBefore && !this._verified);
      this.teaser.setAttribute('aria-expanded', String(!!this.dialog.open));
    }

    _lockScroll() {
      if (this._scrollLock) return;
      const body = document.body;
      this._scrollLock = {
        overflow: body.style.getPropertyValue('overflow'),
        overflowPriority: body.style.getPropertyPriority('overflow'),
        paddingRight: body.style.getPropertyValue('padding-right'),
        paddingPriority: body.style.getPropertyPriority('padding-right')
      };
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      if (scrollbar) body.style.setProperty('padding-right', `${parseFloat(getComputedStyle(body).paddingRight) + scrollbar}px`);
      body.style.setProperty('overflow', 'hidden');
    }

    _unlockScroll() {
      if (!this._scrollLock) return;
      const body = document.body;
      const previous = this._scrollLock;
      if (previous.overflow) body.style.setProperty('overflow', previous.overflow, previous.overflowPriority);
      else body.style.removeProperty('overflow');
      if (previous.paddingRight) body.style.setProperty('padding-right', previous.paddingRight, previous.paddingPriority);
      else body.style.removeProperty('padding-right');
      this._scrollLock = null;
    }

    _setStage(stage, focus = true) {
      // A stored browser flag is never treated as proof of a newsletter signup.
      if (stage === 'reward' && !this.demo && !this._verified) stage = 'email';
      this._stage = stage;
      this.dataset.stage = stage;
      this.querySelectorAll('[data-vp-stage]').forEach((node) => { node.hidden = node.dataset.vpStage !== stage; });
      const stageIndex = ['scratch', 'email', 'reward'].indexOf(stage);
      this.querySelectorAll('.vp-progress span').forEach((node, index) => node.classList.toggle('is-active', index === stageIndex));
      const heading = this.querySelector(`[data-vp-stage="${stage}"] h2`);
      if (heading) {
        if (!heading.id) heading.id = `vp-${stage}-${this.id || this._instanceId || (this._instanceId = Math.random().toString(36).slice(2, 10))}`;
        this.dialog.setAttribute('aria-labelledby', heading.id);
      }
      this.querySelectorAll('[data-vp-step]').forEach((node) => {
        const active = node.dataset.vpStep === stage;
        node.classList.toggle('is-active', active);
        if (active) node.setAttribute('aria-current', 'step');
        else node.removeAttribute('aria-current');
      });
      this._feedback('');
      if (stage === 'reward' && !this.code) this._feedback(this._msg('missing_code'));
      if (focus && this.dialog.open) this._frame(() => this._focusStage());
      this.dispatchEvent(new CustomEvent('vp:stage', { bubbles: true, detail: { stage } }));
    }

    _focusStage() {
      if (!this.dialog.open) return;
      const stage = this.querySelector(`[data-vp-stage="${this._stage}"]`);
      let target = stage?.querySelector('[data-vp-focus], h2, h3');
      if (!target) target = this.querySelector('[data-vp-close]');
      if (target) {
        if (!target.matches('input, button, a, select, textarea, [tabindex]')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }

    _resetCards() {
      this._flippedCards = new Set();
      this._revealed = false;
      this._gameTimer = null;
      this.classList.remove('is-revealed');
      this.gameCards.forEach((card) => {
        card.classList.remove('is-flipped');
        card.setAttribute('aria-pressed', 'false');
        card.removeAttribute('aria-disabled');
        card.setAttribute('aria-label', this._cardLabels.get(card));
        card.disabled = false;
      });
      if (this.revealButton) this.revealButton.hidden = false;
      this._gameProgress();
    }

    _gameProgress() {
      const count = Math.min(3, this._flippedCards.size);
      this.querySelectorAll('[data-vp-game-progress]').forEach((node) => {
        node.textContent = this._msg('progress', {count});
      });
    }

    _flipCard(card, index) {
      if (this._stage !== 'scratch' || this._revealed || this._flippedCards.has(card)) return;
      this._flippedCards.add(card);
      card.classList.add('is-flipped');
      card.setAttribute('aria-pressed', 'true');
      card.setAttribute('aria-disabled', 'true');
      card.setAttribute('aria-label', this._msg('card_revealed', {number: index + 1}));
      this._gameProgress();
      this.dispatchEvent(new CustomEvent('vp:card', { bubbles: true, detail: { index, count: this._flippedCards.size } }));
      if (this._flippedCards.size < 3) return;
      this._revealed = true;
      this.classList.add('is-revealed');
      this.gameCards.forEach((button) => button.setAttribute('aria-disabled', 'true'));
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this._gameTimer = this._later(() => {
        this._gameTimer = null;
        if (this._stage === 'scratch') this._setStage(this._verified ? 'reward' : 'email');
      }, reducedMotion ? 100 : 550);
      this.dispatchEvent(new CustomEvent('vp:reveal', { bubbles: true }));
    }

    _reveal() {
      // Accessible shortcut: no pointer gesture or game completion is required.
      if (this._gameTimer !== null) {
        clearTimeout(this._gameTimer);
        this._timers.delete(this._gameTimer);
        this._gameTimer = null;
      }
      this._revealed = true;
      this.classList.add('is-revealed');
      this._setStage(this._verified ? 'reward' : 'email');
      this.dispatchEvent(new CustomEvent('vp:reveal', { bubbles: true, detail: { skipped: true } }));
    }
    _feedback(message) {
      this.querySelectorAll('[data-vp-feedback]').forEach((node) => { node.textContent = message; });
    }

    async _copyCode() {
      if (!this.code) { this._feedback(this._msg('missing_code')); return; }
      let copied = false;
      try {
        if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(this.code); copied = true; }
      } catch (_) { /* Fall back for local previews and older browsers. */ }
      if (!copied) {
        const field = document.createElement('textarea');
        field.value = this.code;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
        this.dialog.appendChild(field);
        const previous = document.activeElement;
        field.select();
        try { copied = document.execCommand('copy'); } catch (_) { copied = false; }
        field.remove();
        if (previous instanceof HTMLElement) previous.focus({ preventScroll: true });
      }
      if (this._active) this._feedback(copied ? this._msg(this.demo ? 'demo_copied' : 'copied') : this._msg('copy_manual', {code: this.code}));
    }

    reset() {
      if (!this.demo || !this._initialized) return;
      this._timers.forEach(clearTimeout);
      this._timers.clear();
      this._seen = false;
      this._resetCards();
      this.form?.reset();
      this._setStage('scratch');
      if (!this.dialog.open) this.open();
    }
  }

  customElements.define('vitapaw-scratch', VitaPawScratch);
})();


